import { useState } from "react";
import clinicAPI from "../services/clinicApi";

export function useClubLifeInteractions(setPosts, { commentsApi = "client" } = {}) {
  const isStaff = commentsApi === "admin";
  const [commentBusy, setCommentBusy] = useState(false);
  const [openComments, setOpenComments] = useState(null);
  const [commentsByPost, setCommentsByPost] = useState({});

  const toggleLike = async (post) => {
    const wasLiked = Boolean(post.liked_by_me);
    const prevCount = Number(post.likes_count) || 0;
    const optimisticLiked = !wasLiked;
    const optimisticCount = Math.max(0, prevCount + (optimisticLiked ? 1 : -1));

    setPosts((prev) =>
      prev.map((p) =>
        p.id === post.id
          ? {
              ...p,
              liked_by_me: optimisticLiked,
              likes_count: optimisticCount,
            }
          : p,
      ),
    );

    try {
      const toggle = isStaff
        ? clinicAPI.toggleAdminClubLifeLike
        : clinicAPI.toggleClubLifeLike;
      const r = await toggle(post.id);
      setPosts((prev) =>
        prev.map((p) =>
          p.id === post.id
            ? { ...p, liked_by_me: r.liked, likes_count: r.likes_count }
            : p,
        ),
      );
    } catch {
      setPosts((prev) =>
        prev.map((p) =>
          p.id === post.id
            ? { ...p, liked_by_me: wasLiked, likes_count: prevCount }
            : p,
        ),
      );
    }
  };

  const loadComments = async (postId) => {
    if (openComments === postId) {
      setOpenComments(null);
      return;
    }
    setOpenComments(postId);
    if (commentsByPost[postId]) return;
    try {
      const fetchComments = isStaff
        ? clinicAPI.getAdminClubLifePostComments
        : clinicAPI.getClubLifeComments;
      const data = await fetchComments(postId);
      setCommentsByPost((prev) => ({ ...prev, [postId]: data.comments || [] }));
    } catch {
      setCommentsByPost((prev) => ({ ...prev, [postId]: [] }));
    }
  };

  const addComment = async (postId, body, clear, parentId = null) => {
    setCommentBusy(true);
    try {
      const send = isStaff
        ? clinicAPI.addAdminClubLifeComment
        : clinicAPI.addClubLifeComment;
      const r = await send(postId, {
        body,
        ...(parentId ? { parent_id: parentId } : {}),
      });
      setCommentsByPost((prev) => ({
        ...prev,
        [postId]: [...(prev[postId] || []), r.comment],
      }));
      setPosts((prev) =>
        prev.map((p) =>
          p.id === postId ? { ...p, comments_count: r.comments_count } : p,
        ),
      );
      clear();
    } finally {
      setCommentBusy(false);
    }
  };

  return {
    commentBusy,
    openComments,
    commentsByPost,
    toggleLike,
    loadComments,
    addComment,
  };
}
